import {concepts, Group, User} from "fearless-common";
import React, {useState} from "react";
import {Modal} from "react-bootstrap";
import {useField} from "formik";
import {Alert, Loading, Checkboxes, Button} from "fearless-common-ui";
import {BackendForm, } from "../forms";
import {backendEndpoints} from "../../libs/backendEndpoints";
import useGet from "../hooks/useGet";
import {routes} from "../../utils";

function groupToOption(group: Group, currentGroupId: number | undefined) {
    return {
        value: `${group.id}`,
        label: `${group.name} ${!group.is_subscribed ? '- No subscription' : ''} ${group.id === currentGroupId ? '(current)' : ''}`,
        disabled: !group.is_subscribed,
    }
}

function SwitchGroupSelect({ currentGroupId, groups }: { currentGroupId?: number; groups: Group[] }) {
    const fieldName = 'group_id';
    const [,, { setValue }] = useField(fieldName)

    const hasGroups = Boolean(groups?.length && Array.isArray(groups));

    return (
        <>
            {Array.isArray(groups) ? (groups.filter(g => g.is_subscribed).length === 0
                    ? (
                        <Alert variant="info">None of your {concepts.Groups.toLowerCase()} have a {concepts.Subscription.toLowerCase()}.</Alert>
                    ) : (
                        <Checkboxes
                            name={fieldName}
                            label="Select team"
                            onChange={(e) => {
                                setValue(e.value)
                            }}
                            options={hasGroups ? groups?.map((g) => groupToOption(g, currentGroupId)) : []}
                        />
                    )
            ) : <Alert variant="info">You don't have any groups. Create one <a href={routes.groups}>here</a>!</Alert>}
        </>
    )
}

export interface ArrangeViaTeamsProps {
    onSuccess: () => void;
    /** The user's subscription you would like to arrange via team */
    user: User & { groups: Group[] },
}

export default function ArrangeViaTeamsButton({ onSuccess, user }: ArrangeViaTeamsProps) {
    const {
        subscription_per_product: subscriptionPerProduct,
    } = user;
    const [showModal, setShowModal] = useState(false);
    const close = () => setShowModal(false);

    const { data: groups, isLoading, error } = useGet<Group[]>({
        endpoint: backendEndpoints.getUserGroups(user.id),
    });

    return (
        <>
            <Button variant="outline-secondary" className="text-darkest" onClick={() => setShowModal(true)}>
                Select {concepts.Group.toLowerCase()}
            </Button>
            {showModal && (
                // Show the modal to switch to a team subscription
                <Modal
                    show={showModal}
                    onHide={close}
                >
                    <BackendForm
                        initialValues={{group_id: `${subscriptionPerProduct?.photo51?.group?.id}`}}
                        targetMethod="POST"
                        targetEndpoint={backendEndpoints.switchSubscription(user.id)}
                        onSuccess={() => {
                            close();
                            onSuccess();
                        }}
                        showFooter={false}
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Switch {concepts.Group.toLowerCase()}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {isLoading && <Loading />}
                            {error && <Alert variant="danger">{error}</Alert>}
                            {Array.isArray(groups) && (
                                <SwitchGroupSelect
                                  currentGroupId={subscriptionPerProduct?.photo51?.group?.id}
                                  groups={groups}
                                />
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            {Array.isArray(groups) && groups.filter(g => g.is_subscribed).length === 0 ? (
                              <>
                                <Button variant="text" onClick={close}>
                                    Close
                                </Button>
                                <Button variant="primary" href={routes.subscriptions('teams')}>
                                    Go to {concepts.Groups.toLowerCase()}
                                </Button>
                              </>
                            ) : (
                                <BackendForm.Footer showError buttonText="Switch" marginTop={false}/>
                            )}
                        </Modal.Footer>
                    </BackendForm>
                </Modal>
            )}
        </>
    );
}
